import type { FormEvent, ReactNode } from "react";
import { Alert, Button, DetailList, Field, StatusBadge } from "@carepass/ui";
import { ApiError } from "@carepass/api-client";
import { memberRouter } from "../../app/runtime";
import { formatWonAmount } from "./won";

export function navigate(to: string, replace = false) {
  if (!memberRouter.navigate(to, { replace })) return;
  const hash = to.includes("#") ? to.slice(to.indexOf("#")) : "";
  window.requestAnimationFrame(() => {
    if (hash) {
      const target = document.querySelector<HTMLElement>(hash);
      target?.scrollIntoView({ block: "start" });
      target?.focus({ preventScroll: true });
      return;
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  });
}

export function PageHeader({ eyebrow, title, description, action }: {
  readonly eyebrow?: string;
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
}) {
  return (
    <header className="page-heading">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action && <div className="page-heading__action">{action}</div>}
    </header>
  );
}

export function Surface({ children, title, description, action, className = "" }: {
  readonly children: ReactNode;
  readonly title?: string;
  readonly description?: string;
  readonly action?: ReactNode;
  readonly className?: string;
}) {
  return (
    <section className={`surface ${className}`.trim()}>
      {(title || description || action) && (
        <header className="surface__header">
          <div>
            {title && <h2>{title}</h2>}
            {description && <p>{description}</p>}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function EmptyState({ title, description, action }: {
  readonly title: string;
  readonly description: string;
  readonly action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function LoadingState({ label = "정보를 불러오는 중입니다." }: { readonly label?: string }) {
  return <div className="loading-state" role="status"><span className="loading-state__spinner" />{label}</div>;
}

export function ErrorState({ error, onRetry }: { readonly error: unknown; readonly onRetry?: () => void }) {
  const message = error instanceof ApiError ? error.message : "잠시 후 다시 시도해 주세요.";
  return (
    <Alert
      persistent
      variant="danger"
      title="정보를 불러오지 못했습니다"
      action={onRetry && <Button size="small" variant="quiet" onClick={onRetry}>다시 시도</Button>}
    >
      {message}
    </Alert>
  );
}

export function statusVariant(status: string): "neutral" | "waiting" | "processing" | "attention" | "success" | "danger" {
  if (["ACTIVE", "APPROVED", "COMPLETED", "PAID", "ANSWERED", "READY"].includes(status)) return "success";
  if (["FAILED", "REJECTED", "CANCELED", "CLOSED", "PAYMENT_EXPIRED"].includes(status)) return "danger";
  if (["PROCESSING", "REVIEWING", "TRANSFER_PROCESSING", "PAYMENT_PROCESSING", "PAYMENT_UNKNOWN", "TRANSFER_UNKNOWN"].includes(status)) return "processing";
  if (["SUPPLEMENT_REQUIRED", "PAYMENT_FAILED", "TRANSFER_FAILED"].includes(status)) return "attention";
  if (["REGISTERED", "UNDER_REVIEW", "PAYMENT_PENDING", "TRANSFER_SELECTION_PENDING", "TRANSFER_READY", "RECEIVED", "IN_PROGRESS", "APPROVAL_PENDING"].includes(status)) return "waiting";
  return "neutral";
}

export function Status({ children }: { readonly children: string }) {
  return <StatusBadge variant={statusVariant(children)}>{statusLabel(children)}</StatusBadge>;
}

const STATUS_LABELS: Readonly<Record<string, string>> = {
  ACTIVE: "이용 중",
  APPROVED: "승인",
  COMPLETED: "완료",
  PAID: "지급 완료",
  ANSWERED: "답변 완료",
  READY: "준비 완료",
  FAILED: "실패",
  REJECTED: "반려",
  CANCELED: "취소",
  CLOSED: "종료",
  PROCESSING: "처리 중",
  REVIEWING: "심사 중",
  REGISTERED: "심사 대기",
  UNDER_REVIEW: "심사 대기",
  PAYMENT_PENDING: "결제 대기",
  PAYMENT_PROCESSING: "결제 확인 중",
  PAYMENT_FAILED: "결제 실패",
  PAYMENT_UNKNOWN: "결제 결과 확인 중",
  RETRY_PENDING: "결제 재시도 대기",
  PAYMENT_EXPIRED: "결제기한 만료",
  TRANSFER_READY: "송금 준비",
  TRANSFER_SELECTION_PENDING: "결제 완료 · 송금 미선택",
  TRANSFER_PROCESSING: "송금 중",
  TRANSFER_FAILED: "송금 실패",
  TRANSFER_UNKNOWN: "송금 결과 확인 중",
  SUPPLEMENT_REQUIRED: "보완 필요",
  RECEIVED: "접수",
  IN_PROGRESS: "처리 중",
  APPROVAL_PENDING: "승인 대기",
};

const CODE_LABELS: Readonly<Record<string, string>> = {
  INITIAL: "최초 결제", RECURRING: "정기 결제", PRORATED: "요금제 변경 추가 결제", PLAN_CHANGE: "요금제 변경",
  PAYMENT: "결제", TRANSFER: "송금", TRANSACTION: "거래 심사", SUBSCRIPTION: "구독", OTHER: "기타",
  CARD_CANCEL: "카드 결제 취소", BANK_TRANSFER: "계좌 환불", MIXED: "카드 취소 및 계좌 환불",
};

export function codeLabel(value: string | null | undefined): string {
  if (!value) return "—";
  return CODE_LABELS[value] ?? "상세 확인 필요";
}

export function statusLabel(value: string): string {
  return STATUS_LABELS[value] ?? "상태 확인 필요";
}

export function money(value: number | null | undefined): string {
  return formatWonAmount(value);
}

export function dateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const normalized = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? value : `${value}Z`;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? value : new Intl.DateTimeFormat("ko-KR", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Seoul",
  }).format(parsed);
}

export function FormActions({ pending, submitLabel, cancelTo }: {
  readonly pending: boolean;
  readonly submitLabel: string;
  readonly cancelTo?: string;
}) {
  return (
    <div className="form-actions">
      {cancelTo && <Button variant="secondary" onClick={() => navigate(cancelTo)}>취소</Button>}
      <Button type="submit" isLoading={pending}>{submitLabel}</Button>
    </div>
  );
}

export function ConfirmForm({ title, description, confirmLabel, onConfirm }: {
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  readonly onConfirm: (reason: string) => Promise<void>;
}) {
  return (
    <Surface title={title} description={description}>
      <form className="stack" onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        void onConfirm(String(data.get("reason") ?? ""));
      }}>
        <Field control="textarea" label="사유" name="reason" required maxLength={1000} />
        <Button type="submit" variant="danger">{confirmLabel}</Button>
      </form>
    </Surface>
  );
}

export { Alert, Button, DetailList, Field };
